import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowRightLeft } from 'lucide-react';
import type { UrlRedirectToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatDateTime } from '~/lib/format-date';
import { useUrlRedirectsList, useUrlRedirectMutations } from '~/hooks/use-url-redirects';
import { tokenToForm, type UrlRedirectFormValues } from '~/lib/schemas/url-redirect';
import { UrlRedirectEditDialog } from './url-redirect-edit-dialog';

/** 301 URL redirects CRUD surface — parity with the legacy admin grid (From/To/Modified). */
export function UrlRedirectsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useUrlRedirectsList();
	const { remove } = useUrlRedirectMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<UrlRedirectFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<UrlRedirectToken | null>(null);

	const columns = useMemo<SystemGridColumn<UrlRedirectToken>[]>(
		() => [
			{
				key: 'from',
				header: t('system.urlRedirects.columns.from'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.urlf,
				exportValue: r => r.urlf,
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.urlf}>
						{r.urlf}
					</span>
				)
			},
			{
				key: 'to',
				header: t('system.urlRedirects.columns.to'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.urlt,
				exportValue: r => r.urlt,
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={r.urlt}>
						{r.urlt}
					</span>
				)
			},
			{
				key: 'modified',
				header: t('system.urlRedirects.columns.modified'),
				width: '170px',
				sortValue: r => (r.mdt ? String(r.mdt) : ''),
				exportValue: r => formatDateTime(r.mdt),
				render: r => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(r.mdt)}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<UrlRedirectToken>
				gridId="url-redirects"
				title={t('system.urlRedirects.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.urlf}
				columns={columns}
				searchPredicate={(r, q) => `${r.urlf ?? ''} ${r.urlt ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'from', dir: 'asc' }}
				emptyIcon={<ArrowRightLeft size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.urlRedirects.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={r => {
					setEditing(tokenToForm(r));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="url-redirects"
			/>

			<UrlRedirectEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.urlRedirects.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.urlRedirects.deleteBody', { name: deleteRow?.urlf ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
